import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Modal } from "PFComponents/modal";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useBookings } from "PFCore/hooks/queries";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useProfilesPlaceholdersDelete } from "PFCore/hooks/queries/profile/use_profiles_placeholders_delete";
import { PermissionRule, Profile } from "PFCore/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./profile_placeholder_info_box.module.scss";

type ProfilePlaceholderInfoBoxProps = {
  profile: Profile;
  onProfileDeleted: VoidFunction;
};

export const ProfilePlaceholderInfoBox = ({ profile, onProfileDeleted }: ProfilePlaceholderInfoBoxProps) => {
  const { t: tTranslation } = useTranslation("translation");
  const { t } = useTranslation("profiles", { keyPrefix: "preview.placeholderProfile" });
  const { showGrowl } = useGrowlStoreActions();

  const { data: currentProfile } = useCurrentProfile();
  const canDeletePlaceholderProfiles = useIsPermittedTo(currentProfile)(
    PermissionRule.DeletePlaceholderProfiles
  );

  const { data: bookings } = useBookings({
    profileId: profile.id,
    perPage: 0
  });
  const { mutate: deletePlaceholders } = useProfilesPlaceholdersDelete();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const handleDeleteProfile = () => {
    deletePlaceholders([profile.id], {
      onSuccess: () => {
        showGrowl({
          message: t("successMessage")
        });
        onProfileDeleted();
      },
      onError: () => {
        showGrowl({
          message: t("errorMessage"),
          kind: "error"
        });
      }
    });
  };

  const bookingsInfoLabel = useMemo(
    () => t(bookings?.meta?.total ? "hasBookings" : "noBookings", { profileName: getProfileName(profile) }),
    [t, profile, bookings]
  );
  const bookingsDeleteDisclaimer = useMemo(
    () => (bookings?.meta?.total ? t("bookingsWillBeDeleted") : ""),
    [t, bookings]
  );

  return (
    <Tile paddingVariant="lg">
      <Stack gap="spacingLg" alignItems="start">
        <Inline>
          <Icon name="placeholder-profile" />
          <Typography variant="bodyBold">{t("title")}</Typography>
        </Inline>
        <Typography variant="bodyRegular">{bookingsInfoLabel}</Typography>
        {canDeletePlaceholderProfiles && (
          <Button
            kind="secondary"
            icon="remove"
            text={tTranslation("deleteText")}
            onClick={() => setIsDeleteModalVisible(true)}
          />
        )}
        {isDeleteModalVisible && (
          <Modal
            title={t("modalTitle")}
            onOK={handleDeleteProfile}
            labelOK={t("okLabel")}
            kindOK="danger"
            onClose={() => setIsDeleteModalVisible(false)}
          >
            <Stack>
              <Typography variant="bodyRegular">{bookingsInfoLabel}</Typography>
              <Typography variant="bodyRegular">
                {t("modalContent")} {bookingsDeleteDisclaimer}
              </Typography>
              <Typography className={css.disclaimer} variant="labelRegular">
                {t("modalDisclaimer")}
              </Typography>
            </Stack>
          </Modal>
        )}
      </Stack>
    </Tile>
  );
};
